import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Stack from "@mui/material/Stack";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { LEAD_STATUSES, getAssignableUsers } from "app/redux/slice/leadSlice";

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const AddLead = ({ value, onClose, handleSubmit }) => {
  const dispatch = useDispatch();
  const leadData = useSelector((state) => state?.lead);
  const validationSchema = Yup.object({
    name: Yup.string(),
    phoneNumber: Yup.string().required("Please enter phone number"),
    email: Yup.string().email("Please enter a valid email address"),
    reminderDate: Yup.string().when("status", {
      is: "Reminder Follow-up",
      then: (schema) =>
        schema.required("Please select reminder date & time"),
    }),
  });
  const mobileView = useMediaQuery("(max-width:575px)");

  useEffect(() => {
    dispatch(getAssignableUsers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Formik
      initialValues={{
        name: value?.name || "",
        phoneNumber: value?.phoneNumber || "",
        email: value?.email || "",
        address: value?.address || "",
        city: value?.city || "",
        state: value?.state || "",
        country: value?.country || "",
        status: value?.status || LEAD_STATUSES[0],
        assignedTo: value?.assignedTo?._id || value?.assignedTo || "",
        reminderDate: toDateTimeLocal(value?.reminderDate),
        onParty: Boolean(value?.onParty),
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(data, { setSubmitting }) => {
        setSubmitting(true);
        handleSubmit(data);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        handleChange,
        handleBlur,
        setFieldValue,
      }) => (
        <Form>
          {/* Auto-generated at create time and immutable - shown for reference
              only, never submitted. */}
          {value?.leadNumber && (
            <Stack spacing={{ xs: 1, sm: 2 }} direction={{ xs: "column", sm: "row" }} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Lead No"
                value={value.leadNumber}
                disabled
                InputProps={{ readOnly: true }}
              />
            </Stack>
          )}
          <Stack spacing={{ xs: 1, sm: 2 }} direction={{ xs: "column", sm: "row" }} sx={{ mb: 2 }}>
            <JumboAppTextField fullWidth name="name" label="Name" />
            <JumboAppTextField fullWidth name="phoneNumber" label="Phone Number*" />
          </Stack>
          <Stack spacing={{ xs: 1, sm: 2 }} direction={{ xs: "column", sm: "row" }} sx={{ mb: 2 }}>
            <JumboAppTextField fullWidth name="email" label="Email" />
            <FormControl fullWidth>
              <InputLabel id="lead-status-label">Status</InputLabel>
              <Select
                name="status"
                labelId="lead-status-label"
                id="status"
                value={values.status}
                label="Status"
                onChange={handleChange}
                onBlur={handleBlur}
              >
                {LEAD_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
              {touched.status && errors.status && (
                <FormHelperText>{errors.status}</FormHelperText>
              )}
            </FormControl>
          </Stack>
          {values.status === "Reminder Follow-up" && (
            <Stack spacing={{ xs: 1, sm: 2 }} direction={{ xs: "column", sm: "row" }} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Reminder Date & Time*"
                name="reminderDate"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={values.reminderDate}
                onChange={(evt) =>
                  setFieldValue("reminderDate", evt.target.value)
                }
                onBlur={handleBlur}
                error={Boolean(touched.reminderDate && errors.reminderDate)}
                helperText={touched.reminderDate && errors.reminderDate}
              />
            </Stack>
          )}

          <Stack spacing={{ xs: 1, sm: 2 }} direction={{ xs: "column", sm: "row" }} sx={{ mb: 2 }}>
            <JumboAppTextField fullWidth name="city" label="City" />
            <JumboAppTextField fullWidth name="state" label="State" />
            <JumboAppTextField fullWidth name="country" label="Country" />
          </Stack>
          <Stack spacing={{ xs: 1, sm: 2 }} direction={{ xs: "column", sm: "row" }} sx={{ mb: 2 }}>
            <JumboAppTextField fullWidth name="address" label="Address" />
          </Stack>
          <Stack spacing={{ xs: 1, sm: 2 }} direction={{ xs: "column", sm: "row" }} sx={{ mb: 4 }}>
            <FormControl fullWidth>
              <InputLabel id="lead-assigned-label">Assigned To</InputLabel>
              <Select
                name="assignedTo"
                labelId="lead-assigned-label"
                id="assignedTo"
                value={values.assignedTo}
                label="Assigned To"
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {leadData?.assignableUsers?.map((userOption) => (
                  <MenuItem key={userOption._id} value={userOption._id}>
                    {userOption.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ minWidth: 140 }}
              control={
                <Switch
                  name="onParty"
                  color="primary"
                  checked={values.onParty}
                  onChange={(e) => setFieldValue("onParty", e.target.checked)}
                />
              }
              label="On-Party"
            />
          </Stack>
          <Stack
            spacing={{ xs: 1, sm: 2 }}
            direction="row"
            justifyContent="flex-end"
          >
            <LoadingButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              size="large"
              fullWidth={mobileView}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              sx={{ mb: 3 }}
              loading={isSubmitting}
              loadingIndicator={<CircularProgress size={20} />}
              fullWidth={mobileView}
            >
              Save
            </LoadingButton>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default AddLead;
